import { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const TOKEN_KEY = "ticino-wild-token";
const API_BASE = `${process.env.REACT_APP_SERVER_BASE_URL}/superadmin/broadcast`;
const CONFIRM_TEXT = "CONFERMA INVIO";
const POLL_INTERVAL_MS = 2000;
const FINAL_STATUSES = ["completed", "partial", "failed"];

const QUILL_MODULES = {
    toolbar: [
        ["bold", "italic", "underline"],
        [{ header: 2 }, { header: 3 }],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link"],
        ["clean"]
    ]
};

const QUILL_FORMATS = [
    "bold", "italic", "underline",
    "header",
    "list", "bullet",
    "link"
];

const authHeaders = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    return {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`
    };
};

const StatusBadge = ({ status }) => {
    const styles = {
        pending: "bg-yellow-100 text-yellow-800",
        sending: "bg-blue-100 text-blue-800",
        completed: "bg-green-100 text-green-800",
        partial: "bg-orange-100 text-orange-800",
        failed: "bg-red-100 text-red-800"
    };
    return (
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${styles[status] || "bg-neutral-100 text-neutral-800"}`}>
            {status}
        </span>
    );
};

const HandleAdminBroadcast = () => {
    const [subject, setSubject] = useState("");
    const [htmlContent, setHtmlContent] = useState("");
    const [recipientCount, setRecipientCount] = useState(null);
    const [history, setHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);

    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [previewHtml, setPreviewHtml] = useState(null);

    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [confirmStep, setConfirmStep] = useState(1);
    const [confirmInput, setConfirmInput] = useState("");

    const [isSending, setIsSending] = useState(false);
    const [currentBroadcast, setCurrentBroadcast] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");

    const [detailBroadcast, setDetailBroadcast] = useState(null);
    const [isDetailLoading, setIsDetailLoading] = useState(false);

    const pollRef = useRef(null);

    const openDetail = async (broadcastId) => {
        setIsDetailLoading(true);
        setDetailBroadcast({ _id: broadcastId, loading: true });
        try {
            const res = await fetch(`${API_BASE}/${broadcastId}`, { method: "GET", headers: authHeaders() });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            setDetailBroadcast(json.payload);
        } catch (err) {
            console.error(err);
            setDetailBroadcast(null);
            setErrorMessage("Errore durante il recupero dei dettagli del broadcast.");
        } finally {
            setIsDetailLoading(false);
        }
    };

    const fetchRecipientCount = async () => {
        try {
            const res = await fetch(`${API_BASE}/recipient-count`, { method: "GET", headers: authHeaders() });
            if (!res.ok) throw new Error(`Errore recupero destinatari (${res.status})`);
            const json = await res.json();
            setRecipientCount(json.payload?.count ?? 0);
        } catch (err) {
            console.error(err);
            setRecipientCount(null);
        }
    };

    const fetchHistory = async () => {
        setIsLoadingHistory(true);
        try {
            const res = await fetch(API_BASE, { method: "GET", headers: authHeaders() });
            if (!res.ok) throw new Error(`Errore recupero storico (${res.status})`);
            const json = await res.json();
            setHistory(json.payload || []);
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    useEffect(() => {
        fetchRecipientCount();
        fetchHistory();
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, []);

    const stripHtml = useMemo(() => {
        if (!htmlContent) return "";
        const div = document.createElement("div");
        div.innerHTML = htmlContent;
        return (div.textContent || "").trim();
    }, [htmlContent]);

    const isFormValid = subject.trim().length > 0 && stripHtml.length > 0;

    const handlePreview = async () => {
        setErrorMessage("");
        if (!isFormValid) {
            setErrorMessage("Compila oggetto e contenuto prima di procedere.");
            return;
        }
        setIsPreviewLoading(true);
        try {
            const res = await fetch(`${API_BASE}/preview`, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify({ subject: subject.trim(), htmlContent })
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.message || `HTTP ${res.status}`);
            setPreviewHtml(json.payload?.html || "");
        } catch (err) {
            console.error(err);
            setErrorMessage(err.message || "Errore durante la generazione dell'anteprima.");
        } finally {
            setIsPreviewLoading(false);
        }
    };

    const openConfirm = () => {
        setErrorMessage("");
        if (!isFormValid) {
            setErrorMessage("Compila oggetto e contenuto prima di procedere.");
            return;
        }
        setConfirmStep(1);
        setConfirmInput("");
        setIsConfirmOpen(true);
    };

    const closeConfirm = () => {
        setIsConfirmOpen(false);
        setConfirmStep(1);
        setConfirmInput("");
    };

    const startPolling = (broadcastId) => {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
            try {
                const res = await fetch(`${API_BASE}/${broadcastId}`, { method: "GET", headers: authHeaders() });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const json = await res.json();
                const broadcast = json.payload;
                setCurrentBroadcast(broadcast);
                if (FINAL_STATUSES.includes(broadcast.status)) {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                    setIsSending(false);
                    fetchHistory();
                }
            } catch (err) {
                console.error(err);
            }
        }, POLL_INTERVAL_MS);
    };

    const handleSend = async () => {
        if (confirmInput.trim() !== CONFIRM_TEXT) return;
        try {
            const res = await fetch(API_BASE, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify({ subject: subject.trim(), htmlContent })
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.message || `HTTP ${res.status}`);

            const broadcastId = json.payload?.broadcastId;
            const totalRecipients = json.payload?.totalRecipients ?? recipientCount ?? 0;

            setCurrentBroadcast({
                _id: broadcastId,
                subject: subject.trim(),
                status: "pending",
                sentCount: 0,
                failedCount: 0,
                totalRecipients
            });
            setIsSending(true);
            closeConfirm();

            // Reset form per evitare invii accidentali ripetuti
            setSubject("");
            setHtmlContent("");

            startPolling(broadcastId);
        } catch (err) {
            console.error(err);
            setErrorMessage(err.message || "Errore durante l'avvio dell'invio.");
        }
    };

    return (
        <div className="bg-white p-4 rounded-lg w-full max-w-[1000px] lg:min-w-[1000px]">

            {/* Avviso destinatari */}
            <div className="mb-4 p-3 bg-neutral-50 border rounded-md text-sm text-neutral-700">
                <div>
                    {recipientCount === null
                        ? "Calcolo destinatari…"
                        : `Destinatari attualmente eleggibili: ${recipientCount}`}
                </div>
                <div className="mt-1 text-xs text-neutral-500 italic">
                    Utenti registrati con email valida, esclusi account eliminati e quelli in attesa di cancellazione.
                </div>
            </div>

            {/* Subject */}
            <label className="block mb-2 text-sm font-semibold text-[#225424]">
                Oggetto
            </label>
            <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={200}
                placeholder="Es: Aggiornamento Termini e Condizioni"
                className="w-full mb-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#225424]"
                disabled={isSending}
            />
            <div className="text-xs text-neutral-500 mb-4">{subject.length}/200</div>

            {/* Quill */}
            <label className="block mb-2 text-sm font-semibold text-[#225424]">
                Contenuto email
            </label>
            <div className="mb-4">
                <ReactQuill
                    value={htmlContent}
                    onChange={setHtmlContent}
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                    readOnly={isSending}
                    theme="snow"
                />
            </div>

            {/* Errore */}
            {errorMessage && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                    {errorMessage}
                </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-end mb-8">
                <button
                    type="button"
                    onClick={handlePreview}
                    disabled={!isFormValid || isPreviewLoading || isSending}
                    className="px-4 py-2 rounded-md border border-[#225424] text-[#225424] hover:bg-neutral-100 disabled:opacity-50"
                >
                    {isPreviewLoading ? "Anteprima…" : "Anteprima"}
                </button>
                <button
                    type="button"
                    onClick={openConfirm}
                    disabled={!isFormValid || isSending}
                    className="px-4 py-2 rounded-md bg-[#225424] text-white hover:bg-[#1a4019] disabled:opacity-50"
                >
                    Invia a tutti gli utenti
                </button>
            </div>

            {/* Sending status */}
            {isSending && currentBroadcast && (
                <div className="mb-8 p-4 border-2 border-blue-300 bg-blue-50 rounded-md">
                    <div className="font-semibold text-blue-900 mb-2">
                        Invio in corso…
                    </div>
                    <div className="text-sm text-blue-800">
                        {`${currentBroadcast.sentCount || 0} / ${currentBroadcast.totalRecipients || 0} inviate · ${currentBroadcast.failedCount || 0} errori`}
                    </div>
                </div>
            )}

            {/* Risultato finale */}
            {!isSending && currentBroadcast && FINAL_STATUSES.includes(currentBroadcast.status) && (
                <div className="mb-8 p-4 border rounded-md bg-neutral-50">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold">Risultato:</span>
                        <StatusBadge status={currentBroadcast.status} />
                    </div>
                    <div className="text-sm text-neutral-700">
                        {`${currentBroadcast.sentCount || 0} email inviate su ${currentBroadcast.totalRecipients || 0}, ${currentBroadcast.failedCount || 0} errori.`}
                    </div>
                </div>
            )}

            {/* Storico */}
            <div className="border-t pt-4">
                <h3 className="text-lg font-semibold text-[#225424] mb-3">
                    Storico invii
                </h3>
                {isLoadingHistory ? (
                    <div className="text-sm text-neutral-500">Caricamento…</div>
                ) : history.length === 0 ? (
                    <div className="text-sm text-neutral-500">Nessuna comunicazione inviata.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-neutral-100">
                                <tr>
                                    <th className="px-3 py-2 text-left">Data</th>
                                    <th className="px-3 py-2 text-left">Oggetto</th>
                                    <th className="px-3 py-2 text-left">Stato</th>
                                    <th className="px-3 py-2 text-right">Inviate</th>
                                    <th className="px-3 py-2 text-right">Errori</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.slice(0, 10).map((b) => (
                                    <tr
                                        key={b._id}
                                        className="border-t hover:bg-neutral-50 cursor-pointer"
                                        onClick={() => openDetail(b._id)}
                                    >
                                        <td className="px-3 py-2">{new Date(b.createdAt).toLocaleString("it-CH")}</td>
                                        <td className="px-3 py-2 truncate max-w-[260px]">{b.subject}</td>
                                        <td className="px-3 py-2"><StatusBadge status={b.status} /></td>
                                        <td className="px-3 py-2 text-right">{b.sentCount || 0} / {b.totalRecipients || 0}</td>
                                        <td className="px-3 py-2 text-right">{b.failedCount || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal preview */}
            {previewHtml !== null && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center p-4 border-b">
                            <h3 className="font-semibold">Anteprima email</h3>
                            <button
                                type="button"
                                onClick={() => setPreviewHtml(null)}
                                className="text-2xl leading-none px-2"
                                aria-label="Chiudi"
                            >
                                &times;
                            </button>
                        </div>
                        <iframe
                            title="broadcast-preview"
                            srcDoc={previewHtml}
                            sandbox=""
                            className="flex-1 w-full border-0"
                            style={{ minHeight: "60vh" }}
                        />
                    </div>
                </div>
            )}

            {/* Modal conferma 2 step */}
            {isConfirmOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg w-full max-w-md p-6">
                        {confirmStep === 1 ? (
                            <>
                                <h3 className="font-semibold text-lg mb-3">Confermi l'invio?</h3>
                                <p className="text-sm text-neutral-700 mb-2">
                                    {`Stai per inviare l'email a ${recipientCount ?? "—"} utenti.`}
                                </p>
                                <p className="text-sm text-neutral-700 mb-4">
                                    <strong>Oggetto:</strong> {subject}
                                </p>
                                <div className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded p-2 mb-4">
                                    Questa è una comunicazione di servizio: verrà inviata a tutti gli utenti registrati e non potrà essere annullata.
                                </div>
                                <div className="flex justify-end gap-2">
                                    <button type="button" onClick={closeConfirm} className="px-4 py-2 rounded border">
                                        Annulla
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setConfirmStep(2)}
                                        className="px-4 py-2 rounded bg-[#225424] text-white"
                                    >
                                        Procedi
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <h3 className="font-semibold text-lg mb-3">Conferma definitiva</h3>
                                <p className="text-sm text-neutral-700 mb-3">
                                    {`Per confermare l'invio, digita esattamente: ${CONFIRM_TEXT}`}
                                </p>
                                <input
                                    type="text"
                                    value={confirmInput}
                                    onChange={(e) => setConfirmInput(e.target.value)}
                                    placeholder={CONFIRM_TEXT}
                                    className="w-full mb-4 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#225424]"
                                    autoFocus
                                />
                                <div className="flex justify-end gap-2">
                                    <button type="button" onClick={closeConfirm} className="px-4 py-2 rounded border">
                                        Annulla
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSend}
                                        disabled={confirmInput.trim() !== CONFIRM_TEXT}
                                        className="px-4 py-2 rounded bg-red-600 text-white disabled:opacity-50"
                                    >
                                        Invia ora
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Modal dettaglio storico */}
            {detailBroadcast && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center p-4 border-b">
                            <h3 className="font-semibold">Dettaglio invio</h3>
                            <button
                                type="button"
                                onClick={() => setDetailBroadcast(null)}
                                className="text-2xl leading-none px-2"
                                aria-label="Chiudi"
                            >
                                &times;
                            </button>
                        </div>
                        <div className="p-4 overflow-y-auto">
                            {isDetailLoading || detailBroadcast.loading ? (
                                <div className="text-sm text-neutral-500">Caricamento…</div>
                            ) : (
                                <>
                                    <div className="mb-3">
                                        <div className="text-xs text-neutral-500">Oggetto</div>
                                        <div className="font-medium break-words">{detailBroadcast.subject}</div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                                        <div>
                                            <div className="text-xs text-neutral-500">Data invio</div>
                                            <div>{new Date(detailBroadcast.createdAt).toLocaleString("it-CH")}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-neutral-500">Stato</div>
                                            <StatusBadge status={detailBroadcast.status} />
                                        </div>
                                        <div>
                                            <div className="text-xs text-neutral-500">Inviate / totali</div>
                                            <div>{detailBroadcast.sentCount || 0} / {detailBroadcast.totalRecipients || 0}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-neutral-500">Errori</div>
                                            <div>{detailBroadcast.failedCount || 0}</div>
                                        </div>
                                    </div>

                                    {detailBroadcast.failedEmails && detailBroadcast.failedEmails.length > 0 && (
                                        <div className="mt-4 border-t pt-3">
                                            <div className="flex justify-between items-center mb-2">
                                                <h4 className="font-semibold text-sm">
                                                    Utenti con errore di invio ({detailBroadcast.failedEmails.length})
                                                </h4>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const ids = detailBroadcast.failedEmails.map(f => f.userId).join("\n");
                                                        navigator.clipboard?.writeText(ids);
                                                    }}
                                                    className="text-xs px-2 py-1 border rounded hover:bg-neutral-100"
                                                >
                                                    Copia ID
                                                </button>
                                            </div>
                                            <div className="text-xs text-neutral-600 mb-2 italic">
                                                Lista degli userId per cui l'invio è fallito. Da girare allo sviluppatore per la diagnostica.
                                            </div>
                                            <ul className="text-xs font-mono bg-neutral-50 border rounded p-2 max-h-60 overflow-y-auto space-y-1">
                                                {detailBroadcast.failedEmails.map((f, i) => (
                                                    <li key={i} className="break-all">
                                                        <span className="text-neutral-800">{f.userId}</span>
                                                        {f.error && <span className="text-neutral-500"> — {f.error}</span>}
                                                    </li>
                                                ))}
                                            </ul>
                                            {detailBroadcast.failedEmails.length === 100 && (
                                                <div className="text-xs text-orange-600 mt-2">
                                                    Lista limitata ai primi 100 errori.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HandleAdminBroadcast;
